// src/emitter.ts
var Emitter = class {
  constructor() {
    this.map = /* @__PURE__ */ new Map();
  }
  on(event, fn) {
    let set = this.map.get(event);
    if (!set) {
      set = /* @__PURE__ */ new Set();
      this.map.set(event, set);
    }
    set.add(fn);
    return () => this.off(event, fn);
  }
  once(event, fn) {
    const off = this.on(event, ((...args) => {
      off();
      fn(...args);
    }));
    return off;
  }
  off(event, fn) {
    this.map.get(event)?.delete(fn);
  }
  emit(event, ...args) {
    const set = this.map.get(event);
    if (!set) return;
    for (const fn of [...set]) {
      try {
        ;
        fn(...args);
      } catch (err) {
        if (event === "error") continue;
        const errorSet = this.map.get("error");
        if (!errorSet) continue;
        for (const handler of [...errorSet]) {
          try {
            ;
            handler(err);
          } catch {
          }
        }
      }
    }
  }
  removeAll() {
    this.map.clear();
  }
};

// src/logger.ts
var ORDER = { silent: 0, error: 1, warn: 2, info: 3, debug: 4 };
var Logger = class {
  constructor(level = "warn", capacity = 300, prefix = "eyecontact") {
    this.level = level;
    this.capacity = capacity;
    this.prefix = prefix;
    this.buffer = [];
    this.sinks = /* @__PURE__ */ new Set();
  }
  setLevel(level) {
    this.level = level;
  }
  addSink(sink) {
    this.sinks.add(sink);
    return () => this.sinks.delete(sink);
  }
  history() {
    return this.buffer;
  }
  clear() {
    this.buffer = [];
  }
  error(msg, data) {
    this.write("error", msg, data);
  }
  warn(msg, data) {
    this.write("warn", msg, data);
  }
  info(msg, data) {
    this.write("info", msg, data);
  }
  debug(msg, data) {
    this.write("debug", msg, data);
  }
  write(level, msg, data) {
    if (ORDER[this.level] < ORDER[level]) return;
    const entry = data === void 0 ? { t: Date.now(), level, msg } : { t: Date.now(), level, msg, data };
    this.buffer.push(entry);
    if (this.buffer.length > this.capacity) this.buffer.shift();
    for (const sink of this.sinks) {
      try {
        sink(entry);
      } catch {
      }
    }
    if (typeof console !== "undefined") {
      const fn = level === "debug" ? console.debug : console[level];
      if (data === void 0) fn?.(`[${this.prefix}] ${msg}`);
      else fn?.(`[${this.prefix}] ${msg}`, data);
    }
  }
};

// src/attention.ts
var DEFAULT_THRESHOLDS = {
  yawToleranceDeg: 34,
  pitchToleranceDeg: 30,
  presenceYawToleranceDeg: 55,
  presencePitchToleranceDeg: 45,
  gazeTolerance: 0.62,
  blinkThreshold: 0.5
};
var RAD2DEG = 180 / Math.PI;
var clamp = (v, lo, hi) => v < lo ? lo : v > hi ? hi : v;
function eulerFromMatrix(data) {
  if (!data || data.length < 11) return { yawDeg: 0, pitchDeg: 0, rollDeg: 0 };
  const r11 = data[0] ?? 0;
  const r21 = data[1] ?? 0;
  const r22 = data[5] ?? 1;
  const r23 = data[9] ?? 0;
  const r13 = data[8] ?? 0;
  const r33 = data[10] ?? 1;
  const pitch = Math.asin(clamp(-r23, -1, 1));
  const cosPitch = Math.cos(pitch);
  if (Math.abs(cosPitch) < 1e-4) {
    return {
      yawDeg: Math.atan2(-(data[2] ?? 0), r11) * RAD2DEG,
      pitchDeg: pitch * RAD2DEG,
      rollDeg: 0
    };
  }
  return {
    yawDeg: Math.atan2(r13, r33) * RAD2DEG,
    pitchDeg: pitch * RAD2DEG,
    rollDeg: Math.atan2(r21, r22) * RAD2DEG
  };
}
function pick(map, name) {
  return map.get(name) ?? 0;
}
function gazeFromBlendshapes(categories) {
  if (!categories || categories.length === 0) return { gazeX: 0, gazeY: 0, blink: 0 };
  const map = /* @__PURE__ */ new Map();
  for (const c of categories) {
    const name = c.categoryName ?? c.displayName;
    if (name) map.set(name, c.score);
  }
  const lookLeft = (pick(map, "eyeLookOutLeft") + pick(map, "eyeLookInRight")) / 2;
  const lookRight = (pick(map, "eyeLookInLeft") + pick(map, "eyeLookOutRight")) / 2;
  const lookUp = (pick(map, "eyeLookUpLeft") + pick(map, "eyeLookUpRight")) / 2;
  const lookDown = (pick(map, "eyeLookDownLeft") + pick(map, "eyeLookDownRight")) / 2;
  const blinkL = pick(map, "eyeBlinkLeft");
  const blinkR = pick(map, "eyeBlinkRight");
  return {
    gazeX: clamp(lookRight - lookLeft, -1, 1),
    gazeY: clamp(lookUp - lookDown, -1, 1),
    // The *more open* eye (a lower score means a more open eye). Taking the
    // minimum means a wink reads as ~0 and only a real two-eyed blink scores
    // high enough to hold a frame.
    blink: Math.min(blinkL, blinkR)
  };
}
function evaluateFrame(signals, mode, thresholds = DEFAULT_THRESHOLDS) {
  if (!signals.faceFound) return { verdict: "away", reason: "no-face" };
  if (signals.blink >= thresholds.blinkThreshold) {
    return { verdict: "hold", reason: "blink" };
  }
  const yawLimit = mode === "presence" ? thresholds.presenceYawToleranceDeg : thresholds.yawToleranceDeg;
  const pitchLimit = mode === "presence" ? thresholds.presencePitchToleranceDeg : thresholds.pitchToleranceDeg;
  if (Math.abs(signals.yawDeg) > yawLimit || Math.abs(signals.pitchDeg) > pitchLimit) {
    return { verdict: "away", reason: "head-turned" };
  }
  if (mode === "gaze") {
    const drift = Math.hypot(signals.gazeX, signals.gazeY);
    if (drift > thresholds.gazeTolerance) {
      return { verdict: "away", reason: "gaze-away" };
    }
  }
  return { verdict: "attentive", reason: "attentive" };
}
var MAX_FRAME_GAP_MS = 500;
var AttentionMachine = class {
  constructor(opts) {
    this.opts = opts;
    this.lastAttentiveAt = null;
    /** ms banked towards resumeMs. Only meaningful while away. */
    this.credit = 0;
    this.lastFedAt = null;
    this.state = opts.initial ?? "attentive";
  }
  get current() {
    return this.state;
  }
  configure(opts) {
    this.opts = { ...this.opts, ...opts };
  }
  reset(state = "attentive") {
    this.state = state;
    this.lastAttentiveAt = null;
    this.credit = 0;
    this.lastFedAt = null;
  }
  awayForMs(now) {
    if (this.lastAttentiveAt === null) return 0;
    return Math.max(0, now - this.lastAttentiveAt);
  }
  /** How far along a resume is, 0..1. Always 1 while already attentive. */
  get recoveryProgress() {
    if (this.state === "attentive") return 1;
    if (this.opts.resumeMs <= 0) return 1;
    return clamp(this.credit / this.opts.resumeMs, 0, 1);
  }
  result(changed, awayForMs) {
    return { changed, state: this.state, awayForMs, recoveryProgress: this.recoveryProgress };
  }
  feed(verdict, now) {
    const previousFedAt = this.lastFedAt;
    this.lastFedAt = now;
    const dt = previousFedAt === null ? 0 : clamp(now - previousFedAt, 0, MAX_FRAME_GAP_MS);
    if (verdict === "hold") {
      if (previousFedAt !== null && this.lastAttentiveAt !== null) {
        const held = now - previousFedAt;
        if (held > 0) this.lastAttentiveAt += held;
      }
      return this.result(false, this.awayForMs(now));
    }
    if (verdict === "attentive") {
      this.lastAttentiveAt = now;
      if (this.state === "away") {
        const maxStep = Math.max(1, this.opts.resumeMs / 2);
        this.credit += Math.min(dt, maxStep);
        if (this.credit >= this.opts.resumeMs) {
          this.state = "attentive";
          this.credit = 0;
          return this.result(true, 0);
        }
        return this.result(false, this.awayForMs(now));
      }
      return this.result(false, 0);
    }
    if (this.lastAttentiveAt === null) this.lastAttentiveAt = now;
    const awayFor = now - this.lastAttentiveAt;
    if (this.state === "away") {
      const decay = this.opts.recoveryDecay ?? 0.5;
      this.credit = Math.max(0, this.credit - dt * decay);
      return this.result(false, awayFor);
    }
    if (awayFor >= this.opts.graceMs) {
      this.state = "away";
      this.credit = 0;
      return this.result(true, awayFor);
    }
    return this.result(false, awayFor);
  }
};

// src/detector.ts
var DEFAULT_WASM_BASE = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision/wasm";
var DEFAULT_MODEL = "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";
function describeError(err) {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  if (typeof Event !== "undefined" && err instanceof Event) {
    const target = err.target;
    return `${err.type} event${target?.src ? ` for ${target.src}` : ""} (usually a blocked or failed network request)`;
  }
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}
var EMPTY = {
  faceFound: false,
  yawDeg: 0,
  pitchDeg: 0,
  rollDeg: 0,
  gazeX: 0,
  gazeY: 0,
  blink: 0
};
var FaceDetector = class {
  constructor(opts = {}) {
    this.opts = opts;
    this.landmarker = null;
    this.lastTimestamp = -1;
  }
  get ready() {
    return this.landmarker !== null;
  }
  async init() {
    if (this.landmarker) return;
    const wasmBase = this.opts.wasmBasePath ?? DEFAULT_WASM_BASE;
    const model = this.opts.modelAssetPath ?? DEFAULT_MODEL;
    try {
      await this.createLandmarker(wasmBase, model);
    } catch (err) {
      throw new Error(
        `MediaPipe failed to load. Check that the wasm bundle (${wasmBase}) and the model (${model}) are both reachable from the browser; if a proxy or CSP is blocking them, self-host both and pass wasmBasePath and modelAssetPath. Underlying cause: ${describeError(err)}`
      );
    }
  }
  async createLandmarker(wasmBase, model) {
    const vision = await import('@mediapipe/tasks-vision');
    const { FilesetResolver, FaceLandmarker } = vision;
    const fileset = await FilesetResolver.forVisionTasks(wasmBase);
    this.landmarker = await FaceLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: model,
        delegate: this.opts.delegate ?? "GPU"
      },
      runningMode: "VIDEO",
      numFaces: 1,
      // Both of these are what make gaze mode possible. Without blendshapes you
      // only get head pose, which can't tell "watching" from "eyes on phone".
      outputFaceBlendshapes: true,
      outputFacialTransformationMatrixes: true
    });
  }
  detect(video, nowMs) {
    if (!this.landmarker) return EMPTY;
    if (video.readyState < 2) return EMPTY;
    const ts = nowMs <= this.lastTimestamp ? this.lastTimestamp + 1 : nowMs;
    this.lastTimestamp = ts;
    const raw = this.landmarker.detectForVideo(video, ts);
    const landmarks = raw.faceLandmarks?.[0];
    if (!landmarks || landmarks.length === 0) return EMPTY;
    const { yawDeg, pitchDeg, rollDeg } = eulerFromMatrix(
      raw.facialTransformationMatrixes?.[0]?.data
    );
    const { gazeX, gazeY, blink } = gazeFromBlendshapes(raw.faceBlendshapes?.[0]?.categories);
    const signals = {
      faceFound: true,
      yawDeg,
      pitchDeg,
      rollDeg,
      gazeX,
      gazeY,
      blink
    };
    if (this.opts.emitLandmarks) signals.landmarks = landmarks;
    return signals;
  }
  close() {
    try {
      this.landmarker?.close();
    } catch {
    }
    this.landmarker = null;
    this.lastTimestamp = -1;
  }
};

// src/gate.ts
var MAX_CONSECUTIVE_ERRORS = 10;
var IDLE_SIGNALS = {
  faceFound: false,
  yawDeg: 0,
  pitchDeg: 0,
  rollDeg: 0,
  gazeX: 0,
  gazeY: 0,
  blink: 0
};
var EyeContact = class {
  constructor(options = {}) {
    this.emitter = new Emitter();
    this.controllers = /* @__PURE__ */ new Set();
    this.stream = null;
    this.ownsStream = false;
    this.video = null;
    this.ownsVideo = false;
    this.rafId = null;
    this.timerId = null;
    this.running = false;
    this.startPromise = null;
    this.consecutiveErrors = 0;
    this.lastTick = 0;
    this.measuredFps = 0;
    this.state = "idle";
    this.reason = "not-started";
    this.signals = IDLE_SIGNALS;
    this.awayFor = 0;
    this.recovery = 1;
    /**
     * Why the viewer is away for reasons the camera cannot see.
     *
     * A hidden tab or an unfocused window is a definite "not watching" that costs
     * no detection at all. These are fed to the state machine as ordinary `away`
     * verdicts rather than pausing the player directly, so the grace window, the
     * resume credit and the user-pause rules all apply exactly as they do to
     * looking away with your eyes. One set of rules, one place to change them.
     */
    this.externalAway = null;
    this.blurCheckId = null;
    this.onVisibility = () => {
      if (!this.running) return;
      if (this.opts.pauseWhenTabHidden && document.visibilityState === "hidden") {
        this.setExternalAway("tab-hidden");
        this.tick();
        return;
      }
      if (this.externalAway === "tab-hidden") this.setExternalAway(null);
    };
    this.onWindowBlur = () => {
      if (!this.running || !this.opts.pauseWhenWindowBlurred) return;
      if (this.blurCheckId !== null) clearTimeout(this.blurCheckId);
      this.blurCheckId = setTimeout(() => {
        this.blurCheckId = null;
        if (!this.running) return;
        if (typeof document !== "undefined" && document.hasFocus()) return;
        this.setExternalAway("window-blurred");
      }, 0);
    };
    this.onWindowFocus = () => {
      if (this.blurCheckId !== null) {
        clearTimeout(this.blurCheckId);
        this.blurCheckId = null;
      }
      if (this.externalAway === "window-blurred") this.setExternalAway(null);
    };
    // ---- public surface ----------------------------------------------------
    this.on = this.emitter.on.bind(this.emitter);
    this.once = this.emitter.once.bind(this.emitter);
    this.off = this.emitter.off.bind(this.emitter);
    this.opts = {
      fps: 12,
      graceMs: 3e3,
      resumeMs: 400,
      emitLandmarks: false,
      pauseWhenTabHidden: true,
      pauseWhenWindowBlurred: true,
      ...options
    };
    this.mode = options.mode ?? "gaze";
    this.thresholds = { ...DEFAULT_THRESHOLDS, ...options.thresholds };
    this.log = new Logger(options.logLevel ?? "warn");
    if (options.logger) this.log.addSink(options.logger);
    this.log.addSink((e) => this.emitter.emit("log", e));
    this.detector = new FaceDetector({
      wasmBasePath: options.wasmBasePath,
      modelAssetPath: options.modelAssetPath,
      delegate: options.delegate,
      emitLandmarks: this.opts.emitLandmarks
    });
    this.machine = new AttentionMachine({
      graceMs: this.opts.graceMs,
      resumeMs: this.opts.resumeMs,
      ...options.recoveryDecay !== void 0 ? { recoveryDecay: options.recoveryDecay } : {},
      initial: "attentive"
    });
  }
  setExternalAway(next) {
    if (this.externalAway === next) return;
    this.externalAway = next;
    if (next) {
      this.log.debug(`external away: ${next}`);
    } else {
      this.lastTick = 0;
      this.log.debug("external away cleared");
    }
  }
  get snapshot() {
    return {
      state: this.state,
      reason: this.reason,
      signals: this.signals,
      awayForMs: this.awayFor,
      recoveryProgress: this.recovery,
      running: this.running,
      fps: this.measuredFps
    };
  }
  /** The element the camera is rendering into. Point a <video> at it for a preview. */
  get videoElement() {
    return this.video;
  }
  get mediaStream() {
    return this.stream;
  }
  logs() {
    return this.log.history();
  }
  /**
   * Register a player. The returned function unregisters it.
   *
   * Neither attach's disposer nor detach() destroys the controller: you made
   * it, you own it. Re-attaching the same instance after detaching works
   * exactly as you'd expect. Only `gate.destroy()` disposes whatever is still
   * attached at that point.
   */
  attach(controller) {
    this.controllers.add(controller);
    this.log.info(`controller attached: ${controller.name}`);
    return () => this.detach(controller);
  }
  detach(controller) {
    if (!this.controllers.delete(controller)) return;
    this.log.info(`controller detached: ${controller.name}`);
  }
  setMode(mode) {
    if (mode === this.mode) return;
    this.mode = mode;
    this.machine.reset(this.state === "away" ? "away" : "attentive");
    this.log.info(`mode -> ${mode}`);
  }
  getMode() {
    return this.mode;
  }
  setThresholds(next) {
    this.thresholds = { ...this.thresholds, ...next };
    this.log.debug("thresholds updated", this.thresholds);
  }
  getThresholds() {
    return { ...this.thresholds };
  }
  setRecoveryDecay(decay) {
    this.machine.configure({ recoveryDecay: decay });
  }
  setTiming(next) {
    if (next.graceMs !== void 0) this.opts.graceMs = next.graceMs;
    if (next.resumeMs !== void 0) this.opts.resumeMs = next.resumeMs;
    this.machine.configure(next);
    this.log.debug("timing updated", { graceMs: this.opts.graceMs, resumeMs: this.opts.resumeMs });
  }
  setFps(fps) {
    this.opts.fps = Math.max(1, Math.min(60, fps));
  }
  setLogLevel(level) {
    this.log.setLevel(level);
  }
  async start() {
    if (this.running) return;
    if (this.startPromise) return this.startPromise;
    this.startPromise = this.doStart().finally(() => {
      this.startPromise = null;
    });
    return this.startPromise;
  }
  async doStart() {
    this.transition("starting", "not-started");
    try {
      await this.acquireCamera();
      await this.detector.init();
      if (this.state !== "starting") {
        this.log.info("start cancelled while acquiring");
        this.detector.close();
        this.teardownCamera();
        return;
      }
      this.running = true;
      this.machine.reset("attentive");
      this.transition("attentive", "attentive");
      this.emitter.emit("ready");
      this.log.info("gate started");
      if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", this.onVisibility);
      }
      if (typeof window !== "undefined") {
        window.addEventListener("blur", this.onWindowBlur);
        window.addEventListener("focus", this.onWindowFocus);
      }
      if (typeof document !== "undefined" && document.hidden && this.opts.pauseWhenTabHidden) {
        this.setExternalAway("tab-hidden");
      }
      this.scheduleTick();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this.log.error("start failed", { message: error.message, name: error.name });
      this.transition("error", "not-started");
      this.teardownCamera();
      this.emitter.emit("error", error);
      throw error;
    }
  }
  stop() {
    if (!this.running && this.state === "idle") return;
    this.running = false;
    if (this.rafId !== null && typeof cancelAnimationFrame !== "undefined") {
      cancelAnimationFrame(this.rafId);
    }
    if (this.timerId !== null) clearTimeout(this.timerId);
    this.rafId = null;
    this.timerId = null;
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.onVisibility);
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("blur", this.onWindowBlur);
      window.removeEventListener("focus", this.onWindowFocus);
    }
    if (this.blurCheckId !== null) {
      clearTimeout(this.blurCheckId);
      this.blurCheckId = null;
    }
    this.externalAway = null;
    this.detector.close();
    this.teardownCamera();
    this.signals = IDLE_SIGNALS;
    this.awayFor = 0;
    this.recovery = 1;
    this.lastTick = 0;
    this.measuredFps = 0;
    this.consecutiveErrors = 0;
    this.transition("idle", "not-started");
    this.log.info("gate stopped");
  }
  /** Stop and drop every listener and controller. Call this on unmount. */
  destroy() {
    this.stop();
    for (const c of this.controllers) {
      try {
        c.destroy?.();
      } catch {
      }
    }
    this.controllers.clear();
    this.emitter.removeAll();
  }
  // ---- internals ---------------------------------------------------------
  async acquireCamera() {
    if (this.opts.stream) {
      this.stream = this.opts.stream;
      this.ownsStream = false;
    } else {
      if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
        throw new Error(
          "Camera unavailable. getUserMedia needs a secure context: https, or http on localhost."
        );
      }
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: this.opts.videoConstraints ?? {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: "user"
        },
        audio: false
      });
      this.ownsStream = true;
    }
    if (this.opts.videoElement) {
      this.video = this.opts.videoElement;
      this.ownsVideo = false;
    } else {
      const el2 = document.createElement("video");
      el2.setAttribute("playsinline", "");
      el2.muted = true;
      el2.style.display = "none";
      document.body.appendChild(el2);
      this.video = el2;
      this.ownsVideo = true;
    }
    this.video.srcObject = this.stream;
    this.video.muted = true;
    this.video.playsInline = true;
    await this.video.play();
    const el = this.video;
    await new Promise((resolve) => {
      if (el.readyState >= 2) return resolve();
      let timer;
      const done = () => {
        if (timer !== void 0) clearTimeout(timer);
        el.removeEventListener("loadeddata", done);
        resolve();
      };
      el.addEventListener("loadeddata", done);
      timer = setTimeout(done, 3e3);
    });
    this.log.info("camera acquired", {
      width: this.video.videoWidth,
      height: this.video.videoHeight
    });
  }
  teardownCamera() {
    if (this.stream && this.ownsStream) {
      for (const track of this.stream.getTracks()) track.stop();
    }
    if (this.video) {
      this.video.srcObject = null;
      if (this.ownsVideo) this.video.remove();
    }
    this.stream = null;
    this.video = null;
    this.ownsStream = false;
    this.ownsVideo = false;
  }
  scheduleTick() {
    if (!this.running) return;
    const interval = 1e3 / this.opts.fps;
    this.timerId = setTimeout(() => {
      const hidden = typeof document !== "undefined" && document.hidden;
      if (!hidden && typeof requestAnimationFrame === "function") {
        this.rafId = requestAnimationFrame(() => this.tick());
      } else {
        this.tick();
      }
    }, interval);
  }
  tick() {
    if (!this.running || !this.video) return;
    if (this.externalAway) {
      const now2 = performance.now();
      const reason = this.externalAway;
      const t = this.machine.feed("away", now2);
      this.signals = IDLE_SIGNALS;
      this.awayFor = t.awayForMs;
      this.recovery = t.recoveryProgress;
      this.reason = reason;
      if (t.changed) {
        this.transition(t.state, reason);
        void this.applyToControllers(t.state);
      }
      this.emitter.emit("frame", {
        t: now2,
        signals: IDLE_SIGNALS,
        verdict: "away",
        reason,
        awayForMs: t.awayForMs,
        recoveryProgress: t.recoveryProgress,
        state: this.state
      });
      this.scheduleTick();
      return;
    }
    const now = performance.now();
    if (this.lastTick > 0) {
      const dt = now - this.lastTick;
      this.measuredFps = this.measuredFps === 0 ? 1e3 / dt : this.measuredFps * 0.8 + 1e3 / dt * 0.2;
    }
    this.lastTick = now;
    try {
      const signals = this.detector.detect(this.video, now);
      const { verdict, reason } = evaluateFrame(signals, this.mode, this.thresholds);
      const t = this.machine.feed(verdict, now);
      this.signals = signals;
      this.awayFor = t.awayForMs;
      this.recovery = t.recoveryProgress;
      if (verdict !== "hold") this.reason = reason;
      if (t.changed) {
        this.transition(t.state, reason);
        void this.applyToControllers(t.state);
      }
      this.emitter.emit("frame", {
        t: now,
        signals,
        verdict,
        reason,
        awayForMs: t.awayForMs,
        recoveryProgress: t.recoveryProgress,
        state: this.state
      });
      this.consecutiveErrors = 0;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this.consecutiveErrors++;
      this.log.error(
        `detection frame failed (${this.consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS})`,
        { message: error.message }
      );
      this.emitter.emit("error", error);
      if (this.consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
        this.log.error("too many consecutive detection failures, stopping");
        this.stop();
        this.transition("error", "not-started");
        this.emitter.emit(
          "error",
          new Error(
            `Detection failed ${MAX_CONSECUTIVE_ERRORS} times in a row and has been stopped. This usually means the WebGL context was lost (often after the machine slept). Call start() again, or construct the gate with delegate: 'CPU'. Last error: ${error.message}`
          )
        );
        return;
      }
    }
    this.scheduleTick();
  }
  async applyToControllers(state) {
    for (const c of this.controllers) {
      try {
        if (state === "away") {
          await c.pause();
          this.log.debug(`paused ${c.name}`);
        } else {
          if (c.isPausedByUser?.()) {
            this.log.debug(`skipped resume of ${c.name}: paused by user`);
            continue;
          }
          await c.play();
          this.log.debug(`resumed ${c.name}`);
        }
      } catch (err) {
        this.log.warn(`controller ${c.name} failed on ${state}`, err);
      }
    }
  }
  transition(to, reason) {
    if (to === this.state) return;
    const from = this.state;
    this.state = to;
    this.reason = reason;
    const event = { from, to, reason, t: Date.now() };
    this.emitter.emit("state", event);
    if (to === "attentive") this.emitter.emit("attentive", event);
    if (to === "away") this.emitter.emit("away", event);
    this.log.info(`state ${from} -> ${to} (${reason})`);
  }
};

export { AttentionMachine, DEFAULT_MODEL, DEFAULT_THRESHOLDS, DEFAULT_WASM_BASE, Emitter, EyeContact, FaceDetector, Logger, eulerFromMatrix, evaluateFrame, gazeFromBlendshapes };
//# sourceMappingURL=chunk-7QXGZ2S3.js.map
//# sourceMappingURL=chunk-7QXGZ2S3.js.map